import { useState, useEffect, useContext } from "react";
import { createContext } from "react";
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import { db } from "../firebase";
import { AuthContext } from "./AuthContext";

const UserProjectsContext = createContext();

export const UserProjectsContextProvider = ({ children }) => {
  const { currentUser, onDemo } = useContext(AuthContext);
  const [projects, setProjects] = useState([]);

  const addProject = async (projectName) => {
    let minOrder = projects.reduce((a, project) => {
      if (project.order !== undefined) {
        if (project.order < a) {
          return project.order;
        } else {
          return a;
        }
      }
      return a;
    }, 1);
    // ---------------- demo mode only ----------------------------
    if (!currentUser && onDemo) {
      const newDoc = {
        userId: "test",
        name: projectName,
        sheetId: new Date().valueOf().toString(),
        order: minOrder - 1,
      };
      setProjects((prevState) => [
        { ...newDoc, id: new Date().valueOf().toString() },
        ...prevState,
      ]);
      return; //exit function
    } // ---------------- demo mode only ----------------------------

    if (currentUser && !onDemo) {
      // document to be submitted
      const newDocRef = doc(collection(db, "userProjects")); // to get Id when submitted and used to update budgetData
      const newDoc = {
        userId: currentUser.uid,
        name: projectName,
        sheetId: newDocRef.id,
        order: minOrder - 1,
      };
      try {
        await setDoc(newDocRef, newDoc);
        setProjects((prevState) => [
          { ...newDoc, id: newDocRef.id },
          ...prevState,
        ]);
      } catch (err) {}
    }
  };

  const deleteProject = async (id) => {
    // ---------------- demo mode only ----------------------------
    if (!currentUser && onDemo) {
      setProjects(projects.filter((item) => item.id !== id));
      return;
    } // ---------------- demo mode only ----------------------------
    if (currentUser && !onDemo) {
      try {
        await deleteDoc(doc(db, "userProjects", id));
        setProjects(projects.filter((item) => item.id !== id));
      } catch (err) {}
    }
  };

  const updateProjectName = async (id, projectName) => {
    // ---------------- demo mode only ----------------------------
    if (!currentUser && onDemo) {
      const newObject = {
        id: id,
        userId: "test",
        name: projectName,
      };
      setProjects((prevState) => {
        const newState = prevState.map((obj) => {
          let oldObject = { ...obj };
          if (obj.id === id) {
            return newObject;
          } else {
            return oldObject;
          }
        });
        return newState;
      });
      return;
    } // ---------------- demo mode only ----------------------------

    if (currentUser && !onDemo) {
      try {
        await updateDoc(doc(db, "userProjects", id), {
          name: projectName,
        });
        const newObject = {
          id: id,
          userId: currentUser.uid,
          name: projectName,
        };
        setProjects((prevState) => {
          const newState = prevState.map((obj) => {
            let oldObject = { ...obj };
            if (obj.id === id) {
              return newObject;
            } else {
              return oldObject;
            }
          });
          return newState;
        });
      } catch (err) {}
    }
  };

  const updateProjectOrder = async (projects) => {
    if (!currentUser && onDemo) {
      return;
    }
    if (currentUser && !onDemo) {
      try {
        let operationCounter = 0;
        let batch = writeBatch(db);

        projects.forEach(async (project) => {
          const docRef = doc(db, "userProjects", project.id);
          if (project.order !== undefined) {
            batch.update(docRef, { order: project.order });
            operationCounter += 1;
          }
          if ((operationCounter + 1) % 499 == 0) {
            await batch.commit();
            batch = writeBatch(db);
          }
        });
        // final commit
        if (!(operationCounter % 499) == 0) {
          await batch.commit();
        }
      } catch (err) {
        console.log(err);
        return;
      }
    }
  };

  useEffect(() => {
    let list = [];

    // ---------------- demo mode only ----------------------------
    if (!currentUser && onDemo) {
      import("./dummyData.js").then((module) => {
        setProjects(module.dummyProjects);
      });
      return; // exit demo mode function
    } // ---------------- demo mode only ----------------------------

    if (currentUser && !onDemo) {
      const fetchData = async () => {
        const q = query(
          collection(db, "userProjects"),
          where("userId", "==", currentUser.uid)
        );
        const res = await getDocs(q);

        res.forEach((doc) => {
          list.push({ id: doc.id, ...doc.data() });
        });
        const sortedList = [...list]?.sort((project1, project2) => {
          if (project1.order > project2.order) {
            return 1;
          }
          if (project2.order > project1.order) {
            return -1;
          }
          if (project2.order === undefined) {
            return -1;
          }
          return 0;
        });
        setProjects(sortedList);
      };
      fetchData();
    }
  }, [currentUser]);

  return (
    <UserProjectsContext.Provider
      value={{
        projects,
        addProject,
        deleteProject,
        updateProjectName,
        setProjects,
        updateProjectOrder,
      }}
    >
      {children}
    </UserProjectsContext.Provider>
  );
};

export default UserProjectsContext;
